import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { isGuestRole } from "../../redux/slices/users";
import Typography from "../../components/common/text/Typography";
import SelectorIntraverseLogoIcon from "../landingPages/intraverseLandingPage/IntraverseHomeImages/IntraverseLogoIcon2.png";
import { breakpoints } from "../../assets/styles/mediaBreakpoints";
import Button from "../../components/common/button/Button";
import Spotify from "../../features/spotify/Spotify";
import { useStreaming } from "../../context/StreamingContext";
import { UpdateUserBirthday } from "../../api/UserController";
import { setColor, setFont } from "../../assets/styles/styles";
import BirthdayIcon from "../../assets/images/icons/UC_Sprite_Icon_Birthday.png";
import Hallway from "../../features/hallway/Hallway";
import { UpdateBirthday } from "../../redux/actions/UserActions";
import GuestToUser from "../guestConversion/GuestToUser";

const NavigationMenu = (props) => {
    const dispatch = useDispatch();
    const checkGuestRole = useSelector(isGuestRole);
    const tknExpr = useSelector((state) => state?.user?.expiration);
    const token = useSelector((state) => state?.user?.token);
    const userId = useSelector((state) => state?.user?.userId);
    const { isDcvStreaming, updateStreamingMode } = useStreaming();
    const [birthday, setBirthday] = useState(null);
    const [isBirthdayInputValid, setIsBirthdayInputValid] = useState(false);
    const [isHallwayOpen, setIsHallwayOpen] = useState(false);

    const handlePlay = () => {
        // Use the streaming context to determine which service to use
        localStorage.setItem("isDCVPlay", isDcvStreaming);

        // Route based on the streaming service
        if (isDcvStreaming) {
            props.setSelectorScreenPage("play");
        } else {
            props.setSelectorScreenPage("play-vagon");
        }
    };

    // Only functional for dev and staging, not production
    const handlePlayVagon = () => {
        updateStreamingMode(false);
        localStorage.setItem("isDCVPlay", false);
        props.setSelectorScreenPage("play-vagon");
    };

    const handlePlayDCV = () => {
        updateStreamingMode(true);
        localStorage.setItem("isDCVPlay", true);
        props.setSelectorScreenPage("play");
    };

    const typographyStyles = useMemo(
        () => ({
            color: "white",
            textTransform: "uppercase",
            lineHeight: " 0.8",
            cursor: "pointer",
            textAlign: "end",
            marginTop: "10px",
        }),
        []
    );

    const submitBirthday = async () => {
        if (!birthday) {
            console.error("No birthday selected");
            return;
        }
        // Convert DD-MM-YYYY to YYYY-MM-DD
        const [dd, mm, yyyy] = birthday.split("-");
        const formattedDate = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;

        try {
            await UpdateUserBirthday(token, formattedDate, userId, true); // fourth agrument is set to true to ignore birthday update count
            dispatch(UpdateBirthday(birthday));
        } catch (error) {
            console.error("Error updating birthday:", error);
        }
    };

    const handleBirthdayInput = (e) => {
        let value = e.target.value.replace(/[^\d-]/g, "");

        if (value.length === 2 && birthday.length === 1) value += "-";
        if (value.length === 5 && birthday.length === 4) value += "-";
        if (value.length === 3 && value[2] !== "-") value = `${value.slice(0, 2)}-${value.slice(2)}`;
        if (value.length === 6 && value[5] !== "-") value = `${value.slice(0, 5)}-${value.slice(5)}`;
        value = value.slice(0, 10);

        setBirthday(value);

        let valid = /^\d{2}-\d{2}-\d{4}$/.test(value);

        if (valid) {
            const [dd, mm, yyyy] = value.split("-").map(Number);
            const now = new Date();

            if (yyyy < 1900 || yyyy > now.getFullYear() || mm < 1 || mm > 12 || dd < 1 || dd > 31) {
                valid = false;
            } else {
                const enteredDate = new Date(yyyy, mm - 1, dd);

                if (
                    enteredDate.getFullYear() !== yyyy ||
                    enteredDate.getMonth() !== mm - 1 ||
                    enteredDate.getDate() !== dd
                ) {
                    valid = false;
                } else {
                    if (enteredDate > now) valid = false;

                    const thirteenYearsAgo = new Date(now.getFullYear() - 13, now.getMonth(), now.getDate());
                    if (enteredDate <= thirteenYearsAgo) valid = true;
                }
            }
        }

        setIsBirthdayInputValid(valid);
    };

    const BirthdayUpdate = () => (
        <>
            <TitleTextContent>Update Birthday To Continue with Intraverse</TitleTextContent>
            <LabelText>Birthday</LabelText>
            <BirthdayContainer>
                <DateInputContainer>
                    <BirthdayIconStyled src={BirthdayIcon} alt="Birthday Icon" />
                    <TextBirthdayInput
                        type="text"
                        value={birthday || ""}
                        onChange={handleBirthdayInput}
                        placeholder="DD-MM-YYYY"
                        maxLength={10}
                    />
                </DateInputContainer>
                <Button
                    margin="1rem 0rem 1rem 0rem"
                    buttonWidth="100%"
                    variant="PurpleButton"
                    backgroundColor={setColor.brightViolet}
                    label="Update Birthday"
                    onClick={submitBirthday}
                    disabled={!isBirthdayInputValid}
                />
            </BirthdayContainer>
        </>
    );

    return (
        <NavigationMenuContainer orientation={props.orientation}>
            <div>
                <Icon src={SelectorIntraverseLogoIcon} alt="Intraverse Logo" />
            </div>
            {!checkGuestRole && (
                <>
                    <Hallway location="playNow" setIsHallwayOpen={setIsHallwayOpen} isHallwayOpen={isHallwayOpen} />
                    <NavigationTabsContainer>
                        <MenuOption>
                            <Typography onClick={handlePlay} variant="h1" style={typographyStyles}>
                                Play Now
                            </Typography>
                            {process.env.REACT_APP_STREAM_API_ENV !== "production" &&
                                process.env.REACT_APP_STREAM_API_ENV !== "development" && (
                                    <>
                                        <p style={{ color: "white", textAlign: "end", fontSize: "18px" }}>
                                            Current Play Now Default: {isDcvStreaming ? "DCV" : "Vagon"}
                                        </p>
                                        <Typography onClick={handlePlayDCV} variant="h1" style={typographyStyles}>
                                            DCV
                                        </Typography>
                                        <Typography onClick={handlePlayVagon} variant="h1" style={typographyStyles}>
                                            Vagon
                                        </Typography>
                                    </>
                                )}
                        </MenuOption>
                        <MenuOption>
                            <UnStyledLink to="/console/profile">
                                <Typography
                                    variant="h1"
                                    style={{ color: "white", textTransform: "uppercase", lineHeight: " 0.8" }}
                                >
                                    My Console
                                </Typography>
                            </UnStyledLink>
                        </MenuOption>
                        <MenuOption>
                            <Spotify setSpotifyNotPremiumAccount={props.setSpotifyNotPremiumAccount} />
                        </MenuOption>
                    </NavigationTabsContainer>
                    <NavigationTabsContainer>
                        <div>
                            <Typography
                                variant="p"
                                style={{
                                    color: "var(--iconColor)",
                                    fontWeight: "bold",
                                    paddingTop: "5px",
                                    textTransform: "uppercase",
                                }}
                            >
                                ©{new Date().getFullYear()} TerraZero Technologies. All rights reserved.
                            </Typography>
                        </div>
                    </NavigationTabsContainer>
                </>
            )}
            {checkGuestRole && <GuestToUser />}
        </NavigationMenuContainer>
    );
};

export default NavigationMenu;

NavigationMenu.propTypes = {
    orientation: PropTypes.string.isRequired,
    setSelectorScreenPage: PropTypes.func.isRequired,
};

const UnStyledLink = styled(Link)`
    text-decoration: none;
`;

const NavigationMenuContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    z-index: 5;
    height: 100%;
    width: 100%;

    ${(props) => {
        if (props.orientation && props.orientation === "portrait") {
            return `
        padding: 25px;
        transform: rotate(-90deg);
        height:100vw;
        width: 100vh;
      `;
        }
        return `
        transform: none;
        padding: none;
        height: 100%;
        width: 100%;
      `;
    }}
`;

const NavigationTabsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
`;

const MenuOption = styled.div`
    margin-top: 50px;

    @media (max-width: ${breakpoints.tablet}px), (max-height: ${breakpoints.mobile}px) {
        margin-top: 30px;
    }
`;

const Icon = styled.img`
    width: 80px;
    height: 80px;

    @media (max-width: ${breakpoints.tablet}px), (max-height: ${breakpoints.mobile}px) {
        width: 50px;
        height: 50px;
    }
`;

const TitleTextContent = styled.h1`
    color: ${setColor.circularProgress};
    font-family: "Barlow", sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: capitalize;
    letter-spacing: 0.5px;
    text-align: center;
    margin-bottom: 12px;
    margin-top: 12px;

    @media (max-width: 768px) {
        font-size: 24px;
        margin: 10px 0;
    }
`;

const LabelText = styled.p`
    text-align: left;
    color: ${setColor.circularProgress};
    font-family: ${setFont.barlow}, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    margin-top: 1rem;
    margin-left: 5px;
    text-transform: capitalize;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
        margin-top: 5px;
        font-size: 18px;
    }

    @media (max-width: 425px) {
        font-size: 14px;
        margin-top: 5px;
    }
`;

const BirthdayContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    @media (max-width: 768px) {
        margin-bottom: 20px;
    }
`;

const DateInputContainer = styled.div`
    position: relative;
    flex-direction: row;
    -webkit-box-align: center;
    align-items: center;
    background: rgb(27, 27, 35);
    border: 2px solid rgb(80, 76, 92);
    border-radius: 0px;
    width: 100%;
    height: 45px;
    margin: 0.3rem 0rem;
`;

const TextBirthdayInput = styled.input`
    padding: 10px 10px 10px 40px;
    color: white;
    width: 100%;
    background-color: transparent;
    border: 0px solid transparent;
    outline: none;
`;

const BirthdayIconStyled = styled.img`
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    pointer-events: none;
    user-select: none;
`;
